import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { useTranslation } from 'react-i18next';

import BottomSheet from './BottomSheet/BottomSheet';
import Button from '../../../widgets/Button';
import ProfileDropdown from './ProfileDropdown';

import OrgIcon from '../../../../assets/icons/Header/organisation.svg?react';
import Chevron from '../../../../assets/icons/General/chevron.svg?react';
import menuIcon from '../../../../assets/icons/Header/menu-toggle.svg';

import { HeaderProps } from '../../../../shared/types/header';
import { useDeviceType } from '../../../../utilities/common/deviceType';
import { useWidgetsModalContext } from '../../../../shared/providers/WidgetsModalProvider';
import useOrganisationsList from '../../../../utilities/hooks/queryHooks/useOrganizationsList';
import OrganisationFilter from '../../Filters/GeneralFilters/FilterOptions/OrganisationFilter';
import useFilters from '../../../../utilities/hooks/useFilters';
import { GraphIcon } from '../../../../assets/icons/Graph';
import { ChevronBottomIcon } from '../../../../assets/icons/ChevronBottomIcon';
import AskAIButton from '../../AIChatSidebar/AskAIButton';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';

function Header({
  toggleSidebar,
  isSidebarOpen,
  isSidebarCollapsed,
  toggleSidebarCollapsed,
  widgetIds,
}: HeaderProps) {
  const isMobile = useDeviceType();
  const [showOrgSheet, setShowOrgSheet] = React.useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [showCount, setShowCount] = useState(false);
  const [orgId, setOrgId] = useState<number>();

  /*
      To avoid calling API on initial render when filters are not applied
      and only call API when filters are applied or changed by user interaction 
   */
  const isInitialMount = React.useRef(true);

  const { t } = useTranslation();
  const { configData } = useFilterData();
  const canSelectOrganisations = !!configData?.showOrganisationDropdown;

  const handleShowClick = () => setShowOrgSheet(true);
  const handleHide = () => setShowOrgSheet(false);

  const { showOrgFilter, setShowDownloadReport, isLayoutReady } =
    useWidgetsModalContext();

  const { saveFilter, appliedFilters, selectedOrganisations, getFilters } =
    useFilters();

  const totalOrgSelected = Array.from(selectedOrganisations.values()).filter(
    Boolean,
  ).length;

  const { data: orgList } = useOrganisationsList(showOrgFilter, {
    enable: false,
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    if (appliedFilters) {
      setFilterApplied(true);
    }
    if (filterApplied) {
      if (totalOrgSelected === 0) setShowCount(false);
      else setShowCount(true);
    }
  }, [filterApplied, appliedFilters]);

  const otherDependencies = useMemo(() => {
    const { dateRange, ...other } = getFilters();
    return other;
  }, [getFilters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
  }, [
    isLayoutReady,
    appliedFilters.dateRange.fromDate,
    appliedFilters.dateRange.toDate,
    ...Object.values(otherDependencies),
  ]);

  useEffect(() => {
    const org = localStorage.getItem('org_id');
    if (org) {
      setOrgId(+org);
    }
  }, []);

  return (
    <div
      className={`relative flex h-14 w-full items-center justify-between border-b border-taupe-500 bg-taupe-50 px-6 md:h-[72px] ${isSidebarCollapsed ? 'md:pl-[calc(5.5rem+1.5rem)]' : 'md:pl-[calc(288px+1.5rem)]'}`}
    >
      <div className="flex items-center">
        <button onClick={toggleSidebar} type="button" className="md:hidden">
          <span className="sr-only">Toggle sidebar</span>
          <img src={menuIcon} alt="" />
        </button>
        <div className="lg:hidden">
          {canSelectOrganisations && (
            <button
              type="button"
              className={`group hover:bg-black-100 active:bg-black-200 flex cursor-pointer items-center gap-x-2 rounded-[36px] px-2 py-1 transition-colors duration-150 lg:hidden`}
              onClick={handleShowClick}
            >
              <OrgIcon
                className={`stroke-blue-900 group-hover:stroke-blue-950`}
              />
              <span
                className={`text-sm font-semibold text-blue-900 capitalize group-hover:text-blue-950`}
              >
                {t('organisation')}
              </span>
              {showCount && (
                <span className="bg-primary-600 flex h-[20px] w-[38px] items-center justify-center rounded-full text-xs font-semibold text-white">
                  {totalOrgSelected}
                </span>
              )}
              <Chevron
                className={`stroke-black-600 group-hover:stroke-black-900`}
              />
            </button>
          )}

          {isMobile && (
            <BottomSheet show={showOrgSheet} onHide={handleHide}>
              <div className="mb-[88.67px] max-h-[68vh] overflow-auto pb-2">
                <OrganisationFilter
                  mobileSheet={showOrgSheet}
                  data={
                    orgList?.sort((a: any, b: any) =>
                      a.name.localeCompare(b.name),
                    ) ?? []
                  }
                  callBackDept={() => {}}
                  callBackRegion={() => {}}
                />
              </div>
              <div className="border-black-200 absolute bottom-0 m-auto flex w-full justify-center border-t py-6">
                <Button
                  className="w-full max-w-[90%]"
                  onClick={() => {
                    handleHide();
                    saveFilter();
                    setFilterApplied(true);
                  }}
                >
                  {t('APPLY')}
                </Button>
              </div>
            </BottomSheet>
          )}
        </div>

        {canSelectOrganisations && (
          <Popover className="relative hidden lg:block">
            {({ open }) => (
              <>
                <PopoverButton
                  className="outline-0"
                  onClick={() => setFilterApplied(false)}
                >
                  <div
                    className={`group flex cursor-pointer items-center gap-x-2 rounded-[36px] px-2 py-1 transition-colors duration-150 ${
                      !open && 'hover:bg-black-100'
                    } active:bg-black-200 ${open && 'bg-black-200'}`}
                  >
                    <OrgIcon
                      className={`stroke-blue-900 group-hover:stroke-blue-950 ${
                        open && 'stroke-black-900'
                      }`}
                    />
                    <span
                      className={`text-sm font-semibold text-blue-900 capitalize group-hover:text-blue-950 ${
                        open && 'text-black-900'
                      }`}
                    >
                      {t('organisation')}
                    </span>
                    {showCount && (
                      <span className="bg-primary-600 flex h-[20px] w-[38px] items-center justify-center rounded-full text-xs font-semibold text-white">
                        {totalOrgSelected}
                      </span>
                    )}
                    <ChevronBottomIcon
                      className={`stroke-blue-900 transition-transform duration-300 ease-out ${open ? 'rotate-180' : 'rotate-0'}`}
                    />
                  </div>
                </PopoverButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <PopoverPanel className="org-dropdown group border-black-200 absolute left-[141px] mt-3 w-[284px] -translate-x-1/2 transform rounded-lg border bg-white shadow-[0px_12px_16px_-4px_#10182814]">
                    <div className="py-1">
                      <div className="mb-20 max-h-[596px]">
                        <OrganisationFilter
                          data={orgList}
                          callBackDept={() => {}}
                          callBackRegion={() => {}}
                        />
                      </div>
                    </div>
                    <div className="border-black-200 absolute bottom-0 m-auto flex w-full justify-center border-t py-4">
                      <PopoverButton
                        as={Button}
                        className="w-full max-w-[90%]"
                        onClick={() => {
                          saveFilter();
                          setFilterApplied(true);
                        }}
                      >
                        {t('APPLY')}
                      </PopoverButton>
                    </div>
                  </PopoverPanel>
                </Transition>
              </>
            )}
          </Popover>
        )}
      </div>
      <div className="flex items-center gap-x-4">
        {orgId && (
          <div className="mr-3 flex w-fit items-center justify-center rounded-full bg-red-500 px-3 py-1 text-xs text-white">{`Viewing as ${orgId}`}</div>
        )}
        {orgId && <AskAIButton />}
        <Button
          variant="outline"
          className="hidden md:flex"
          onClick={() => setShowDownloadReport(true)}
          leftIcon={<GraphIcon />}
        >
          {t('DOWNLOAD_REPORTS')}
        </Button>
        <ProfileDropdown />
      </div>
    </div>
  );
}

export default Header;
